import { FunctionComponent, memo } from "react";
import urlJoin from "url-join";
import { AxiosError } from "axios";
import { Markdown, SpinnerUntil } from "@edgetier/components";
import { Url } from "@edgetier/types";
import { useDispatch } from "react-redux";

import useSimpleQuery from "queries/use-simple-query";
import { getUserId } from "utilities/local-storage";
import { INote } from "redux/application.types";
import { IUser } from "redux/application.types";
import { toastOperations } from "redux/modules/toast";

import QueryHistoryNoteControls from "./query-history-note-controls";
import { IProps } from "./query-history-note.types";
import { STALE_MILLISECONDS } from "./query-history.constants";
import "./query-history-note.scss";

/**
 * Display the contents and details of a note. Two requests need to be made; one for the note itself and a second for
 * the user who wrote the note.
 * @param props.noteId Note identifier.
 * @returns            Note text and details.
 */
const QueryHistoryNote: FunctionComponent<IProps> = ({ noteId }) => {
    const dispatch = useDispatch();

    /**
     * Show server errors in a toast.
     * @param serverError Backend error.
     */
    const onError = (serverError: AxiosError) => {
        dispatch(toastOperations.showServerErrorToast("Note Request Failed", serverError));
    };

    // Request the note.
    const noteQueryOptions = { onError, staleTime: STALE_MILLISECONDS };
    const noteQuery = useSimpleQuery<INote>(urlJoin(Url.Notes, noteId.toString()), noteQueryOptions);

    // Request the user when the note has been retrieved.
    const enabled = typeof noteQuery.data?.userId === "number";
    const userQueryOptions = { enabled, onError, staleTime: STALE_MILLISECONDS };
    const userUrl = urlJoin(Url.Users, String(noteQuery.data?.userId));
    const userQuery = useSimpleQuery<IUser>(userUrl, userQueryOptions);

    return (
        <SpinnerUntil<{ note: INote; user: IUser }>
            data={{ note: noteQuery.data, user: userQuery.data }}
            isReady={typeof noteQuery.data !== "undefined" && typeof userQuery.data !== "undefined"}
        >
            {({ note, user }) => (
                <div className="query-history-note">
                    <div className="query-history-note__header">
                        <strong className="query-history-note__user">
                            {user.firstName} {user.surname}
                        </strong>
                        Note
                    </div>

                    <Markdown markdownString={note.note} />

                    {getUserId() === note.userId && (
                        <QueryHistoryNoteControls queryId={note.queryId} noteId={note.noteId} />
                    )}
                </div>
            )}
        </SpinnerUntil>
    );
};

export default memo(QueryHistoryNote);
